import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { ParticleField } from "@/components/site/ParticleField";
import { useConfig } from "@/context/ConfigContext";
import { useDemo } from "@/context/DemoContext";
import { scrollToId } from "@/hooks/useLenis";

const easing = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="line-mask">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: easing }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const { config } = useConfig();
  const { launchDemo } = useDemo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const headline = config?.hero_headline || "Engineering Certainty.";
  const sub =
    config?.hero_subheadline ||
    "Building intelligent software that transforms complex information into confident decisions.";
  const paragraph =
    config?.hero_paragraph ||
    "Cogentis Technologies develops enterprise software connecting legal, healthcare, insurance, public safety, and AI-powered operations into one intelligent ecosystem.";

  return (
    <section
      id="hero"
      ref={ref}
      className="relative flex min-h-[100svh] items-center overflow-hidden pt-28"
      data-testid="hero-section"
    >
      {/* Backgrounds */}
      <div className="absolute inset-0 -z-10 hero-radial" />
      <div className="absolute inset-0 -z-10 grid-lines" />
      <div className="absolute inset-0 -z-10">
        <ParticleField className="h-full w-full opacity-70" />
      </div>

      <motion.div
        style={{ y, opacity }}
        className="mx-auto w-full max-w-7xl px-6 lg:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="font-mono mb-6 flex items-center gap-3 text-xs tracking-[0.3em] text-white/50"
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: "var(--accent)", boxShadow: "0 0 12px var(--accent-glow)" }}
          />
          COGENTIS TECHNOLOGIES
        </motion.p>

        <h1 className="font-display text-5xl font-light leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.15}>
            <span className="text-glow">{headline}</span>
          </MaskedLine>
        </h1>

        <div className="mt-8 max-w-2xl">
          <MaskedLine delay={0.4}>
            <p className="font-display text-xl font-light text-white/90 sm:text-2xl">
              {sub}
            </p>
          </MaskedLine>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/55"
        >
          {paragraph}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            onClick={launchDemo}
            data-testid="hero-primary-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 font-medium text-white transition-transform duration-200 hover:scale-[1.03]"
            style={{ background: "var(--accent)", boxShadow: "0 0 30px var(--accent-glow)" }}
          >
            Experience Crash Ready
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToId("company")}
            data-testid="hero-secondary-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-7 py-4 font-medium text-white/90 transition-colors duration-200 hover:border-white/40 hover:bg-white/5"
          >
            Learn About Cogentis
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("company")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/40 transition-colors hover:text-white/80 lg:flex"
        aria-label="Scroll down"
      >
        <span className="font-mono text-[10px] tracking-[0.3em]">SCROLL</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.button>
    </section>
  );
}
